import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  Button,
  TextField,
  CircularProgress,
  FormControlLabel,
  Box,
  Checkbox,
  Snackbar,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { createUserCcpAdmin } from "../../../../config/firebase";
import { getAuth, fetchSignInMethodsForEmail } from "firebase/auth";
import ModalDialogTitle from "../../../common/ModalDialogTitle";
import ModalActions from "../../../common/ModalActions";

const AddForm = ({ onSuccess }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false); // State variable for loading
  const [techPrepAdminFlag, setTechPrepAdminFlag] = useState(false);
  const [ccpAdminFlag, setCcpAdminFlag] = useState(false);
  const [error, setError] = useState("");
  const [openAdd, setOpenAdd] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // Function to format phone number with dashes as the user types
  const formatPhoneNumber = (input) => {
    // Remove non-numeric characters
    const cleaned = ("" + input).replace(/\D/g, "");

    // Apply phone number formatting
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `${match[1]}-${match[2]}-${match[3]}`;
    }
    return input;
  };

  const handlePhoneChange = (e) => {
    setPhone(formatPhoneNumber(e.target.value));
  };

  const handleAddOpen = () => {
    setOpenAdd(true);
  };

  const handleAddClose = () => {
    setOpenAdd(false);
    setName("");
    setEmail("");
    setPhone("");
    setPassword("");
    setTechPrepAdminFlag(false);
    setCcpAdminFlag(false);
    setError("");
  };

  const handleSubmit = async () => {
    setLoading(true);
    const auth = getAuth();
    try {
      const signInMethods = await fetchSignInMethodsForEmail(auth, email);
      if (signInMethods.length > 0) {
        setError("Email already exists.");
        setSnackbarMessage("Email already exists.");
        setSnackbarOpen(true);
        setLoading(false);
        return;
      }

      await createUserCcpAdmin(
        email,
        password,
        name,
        phone,
        techPrepAdminFlag,
        ccpAdminFlag
      );
      if (typeof onSuccess === "function") {
        onSuccess();
      }
      setSnackbarMessage("User has been created");
      setSnackbarOpen(true);
      handleAddClose();
    } catch (error) {
      setError(error.message);
      setSnackbarMessage(error.message);
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <div>
      <Button
        variant="contained"
        color="primary"
        startIcon={<AddIcon />}
        onClick={handleAddOpen}
        style={{ marginBottom: 16, marginTop: 10 }}
      >
        Add New User
      </Button>

      <Dialog open={openAdd} onClose={handleAddClose}>
        <ModalDialogTitle onClose={handleAddClose}>
          Add New User
        </ModalDialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            type="text"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Phone"
            type="text"
            fullWidth
            value={phone}
            onChange={handlePhoneChange}
          />
          <TextField
            margin="dense"
            label="Password"
            type="password"
            fullWidth
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={techPrepAdminFlag}
                onChange={(e) => setTechPrepAdminFlag(e.target.checked)}
              />
            }
            label="Tech Prep Admin"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={ccpAdminFlag}
                onChange={(e) => setCcpAdminFlag(e.target.checked)}
              />
            }
            label="CCP Admin"
          />
          {loading && (
            <Box display="flex" justifyContent="center" mt={2}>
              <CircularProgress />
            </Box>
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <ModalActions onSave={handleSubmit} saving={loading} saveLabel="Add" />
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={error ? "error" : "success"}
          sx={{ width: "100%", backgroundColor: "black", color: "white" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default AddForm;
